import { Component, inject } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { of } from "rxjs";
import { ModalService } from "../../services/modal/modal.service";
import { CommonModule } from "@angular/common";
import { EmailsService } from "../../services/api/emails/emails.service";
import { UsersService } from "../../services/api/users/users.service";
import { AuthService } from "../../services/api/auth/auth.service";

@Component({
  selector: "app-welcome",
  imports: [CommonModule],
  templateUrl: "./welcome.html",
  styleUrl: "./welcome.scss",
})
export class Welcome {
  modalService = inject(ModalService);
  private emailsService = inject(EmailsService);
  private usersService = inject(UsersService);
  private authService = inject(AuthService);

  constructor() {
    // Bootstrap gate: grower/entity see the welcome modal once. Managers and
    // admins aren't auto-enrolled in email and don't interact with email
    // subscriptions, so the modal is scoped to those two roles.
    // Seen-state is fetched fresh (not read from the JWT, which would go stale
    // the instant they dismiss) so it can't re-show within the same session.
    if (this.authService.loggedIn) {
      const role = this.authService.getRole();
      if (role === "GROWER" || role === "ENTITY") {
        this.usersService.getWelcomeSeen().subscribe({
          next: ({ welcome_seen_at }) => {
            if (!welcome_seen_at) {
              this.modalService.welcomeModal.set(true);
            }
          },
          error: (err) =>
            console.error("Failed to check welcome-seen status", err),
        });
      }
    }
  }

  // Whether this grower/entity was auto-enrolled in any email at signup.
  // Drives the enrollment note in the modal. Only fetched while it's open.
  readonly hasSignupDefaultResource = rxResource({
    // Signal read must live in `params` — `stream` is not reactive, so the
    // fetch would never re-run when the modal opens (same gotcha as the
    // email-preferences modal).
    params: () => this.modalService.welcomeModal(),
    stream: ({ params: isOpen }) =>
      isOpen ? this.emailsService.hasSignupDefault() : of(false),
  });

  closeModal() {
    // Persist the seen-state so we know the user has actually seen the modal
    // and don't show it again. Fire-and-forget — the modal closes immediately;
    // if the write fails it's re-attempted on the next first-run check.
    this.usersService.markWelcomeSeen().subscribe({
      error: (err) =>
        console.error("Failed to mark welcome modal as seen", err),
    });
    this.modalService.welcomeModal.set(false);
  }

  openEmailPreferences() {
    this.closeModal();
    this.modalService.emailPreferencesModal.set(true);
  }
}
